// TypeScript mirrors of the metrics domain types in
// `packages/roboto/src/roboto/domain/metrics/record.py`. Field names match the
// wire format exactly: `created`/`modified`, `published`/`published_by`,
// `min_timestamp_ns`/`max_timestamp_ns`, snake_case throughout.

export const PERIODS = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "yearly",
] as const;
export type Period = (typeof PERIODS)[number];

export const NUMERIC_AGGREGATIONS = [
  "sum",
  "mean",
  "max",
  "min",
  "count",
] as const;
export type NumericAggregation = (typeof NUMERIC_AGGREGATIONS)[number];

// Wire values from the backend `MetricTimeFilter` StrEnum: the Pydantic
// validator accepts the enum *value* (lowercase), not the Python attribute
// name. Keep these as the literal strings the server expects.
export const METRIC_TIME_FILTERS = ["start_time", "end_time"] as const;
export type MetricTimeFilter = (typeof METRIC_TIME_FILTERS)[number];

export const CHART_DISPLAY_TYPES = [
  { value: "line", label: "Line chart" },
  { value: "bar", label: "Bar chart" },
] as const;
export type ChartDisplayType = (typeof CHART_DISPLAY_TYPES)[number]["value"];

/**
 * Hard ceiling on the number of data points a single metric query or list call
 * can return as a single page. Mirrors `MAX_METRIC_LIST_RESULTS` in the SDK.
 */
export const MAX_METRIC_LIST_RESULTS = 10_000;

export interface MetricDefinitionRecord {
  org_id: string;
  metric_id: string;
  /** Unique (per-org) name for this metric. */
  name: string;
  description: string | null;
  created_by: string;
  created: string; // ISO 8601 with TZ
  modified_by: string;
  modified: string; // ISO 8601 with TZ
}

export interface MetricRecord {
  org_id: string;
  metric_id: string;
  // Fully qualified, dot-separated metric name resolved server-side from the
  // associated `MetricDefinitionRecord`. Mirrors the `name` field added to the
  // Python `MetricRecord` in the SDK.
  name: string;
  session_id: string;
  // The source session's raw int-nanosecond bounds (Unix epoch ns). `null`
  // until the session has at least one file contribution. Mirror of
  // SessionRecord.min_timestamp_ns / max_timestamp_ns.
  min_timestamp_ns: bigint | null;
  max_timestamp_ns: bigint | null;
  value: number;
  device_id?: string | null;
  invocation_id?: string | null;
  published_by: string;
  published: string; // ISO 8601 with TZ
}

export interface NumericAggregateMetricRecord {
  metric_id: string;
  name: string;
  period: Period;
  // Inclusive start / exclusive end of this period bucket, in Unix-epoch
  // nanoseconds (UTC).
  start_time: bigint;
  end_time: bigint;
  total: number;
  value: number;
  aggregation: NumericAggregation;
}

export interface NumericAggregateMetricsResponse {
  aggregation: NumericAggregation;
  records: NumericAggregateMetricRecord[];
}

// -------- Request shapes --------

export interface CreateMetricDefinitionRequest {
  name: string;
  description?: string;
}

export interface UpdateMetricDefinitionRequest {
  description?: string;
}

export interface MetricEntry {
  name: string;
  value: number;
}

export interface PublishMetricsRequest {
  session_id: string;
  /** Omit entirely to let the server infer from the session's attached devices.
   *  Pass `null` to record without a device. Pass a string to bind to a device. */
  device_id?: string | null;
  metrics: MetricEntry[];
}

export interface PublishMetricsError {
  name: string;
  error: string;
}

export interface PublishMetricsResponse {
  succeeded: MetricRecord[];
  failed: PublishMetricsError[];
}

export interface QueryMetricsRequest {
  name: string;
  time_filter?: MetricTimeFilter;
  /** Inclusive window start, Unix-epoch ns. `bigint` to preserve full ns
   *  precision end-to-end (the backend field is an arbitrary-precision `int`);
   *  `MetricsService.queryMetrics` serializes it as a bare integer literal via
   *  `rawJSON` rather than a lossy `number`. */
  start_time_ns?: bigint;
  /** Exclusive window end, Unix-epoch ns. See `start_time_ns`. */
  end_time_ns?: bigint;
  max_results?: number;
  include_device_ids?: string[] | null;
  /** Server-side: only a list is accepted; `null` is rejected at validation time
   *  because `session_id` is non-nullable in the DB. */
  include_session_ids?: string[];
  include_invocation_ids?: string[] | null;
}

export interface AggregateMetricsRequest {
  name: string;
  period: Period;
  aggregation: NumericAggregation;
  time_filter?: MetricTimeFilter;
  start_time_ns: number;
  end_time_ns: number;
  include_device_ids?: string[] | null;
  include_session_ids?: string[];
  include_invocation_ids?: string[] | null;
}
